/**
 * DM-7185: decision logic for starting and cancelling an account deletion from
 * the User Enforcement page (and the cancel action the deletion-request queue
 * shares with it).
 *
 * Split out for the reason broadcasts.logic.ts is: apps/admin-ui is never
 * ESLint'd or complexity-checked in CI and its jest suite matches `.ts` only,
 * so pure logic in a sibling file is the one part of a page that can be tested.
 */

import type { PendingActionItem, UserDeletionReason, UserDeletionState } from '../api/adminOps';

/** Mirrors ACCOUNT_DELETION_NOTE_MAX_LENGTH in @donatemate/admin-operations-models. */
export const NOTE_MAX_LENGTH = 1000;
/** Mirrors the validator's MAX_EMAIL_LENGTH. */
export const EMAIL_MAX_LENGTH = 254;

const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
/** Same shape the validator applies, so a typo is caught before it is audited. */
const EMAIL_SHAPE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function isUuid(value: string): boolean {
  return UUID_PATTERN.test(value);
}

// ---------------------------------------------------------------------------
// Deep link: /enforcement?user=<uuid>
// ---------------------------------------------------------------------------

const ENFORCEMENT_USER_PARAM = 'user';

/** Where the deletion-request queue sends an operator to act on one account. */
export function enforcementHref(userId: string): string {
  return `/enforcement?${ENFORCEMENT_USER_PARAM}=${encodeURIComponent(userId)}`;
}

/**
 * The account a deep link names, or '' when it names none. Anything that is
 * not a UUID is dropped rather than looked up: the id routes answer 400 to it,
 * and a pasted URL must not turn into an audited read of nothing.
 */
export function userIdFromSearch(search: string): string {
  const candidate = (new URLSearchParams(search).get(ENFORCEMENT_USER_PARAM) ?? '').trim();
  return isUuid(candidate) ? candidate : '';
}

// ---------------------------------------------------------------------------
// Lookup by email
// ---------------------------------------------------------------------------

/**
 * Shown under the field whether or not a lookup has run: the operator's most
 * natural first test is their own address, which can never resolve.
 */
export const LOOKUP_HELPER_TEXT =
  'Use the address the donor signs in to the app with. Admin sign-in and app accounts are separate, so your own admin address will not resolve.';

/** Why the lookup cannot be submitted yet, or null when it can. */
export function lookupEmailBlocker(email: string): string | null {
  const trimmed = email.trim();
  if (trimmed.length === 0) return 'Enter an email address';
  if (trimmed.length > EMAIL_MAX_LENGTH || !EMAIL_SHAPE.test(trimmed)) {
    return 'Enter a valid email address';
  }
  return null;
}

/**
 * What to say when a lookup fails. 404 is an answer ("nobody"); everything
 * else is the absence of one, and must not read as "no such account" -- an
 * operator told that would tell the donor they have nothing to delete.
 *
 * Never includes the address: the message is rendered in an aria-live region
 * and the address has no business being repeated anywhere.
 */
export function lookupFailureMessage(error: unknown): string {
  const status =
    typeof error === 'object' && error !== null ? (error as { status?: unknown }).status : null;
  if (status === 404) {
    return 'No app account uses that address. Check the spelling, and remember that an admin sign-in address is not an app account.';
  }
  if (status === 403) {
    return 'Your role is not allowed to look accounts up by email.';
  }
  return 'The lookup could not be completed, so it is not known whether an account uses that address. Try again.';
}

// ---------------------------------------------------------------------------
// Note + reason validation
// ---------------------------------------------------------------------------

/**
 * Why a required operator note cannot be submitted yet, or null.
 *
 * Length is measured on the RAW string because the validator does the same:
 * the raw value is what gets stored, so one visible character followed by
 * padding must not slip past the limit here and then 400 on the server.
 */
export function noteBlocker(note: string): string | null {
  if (note.trim().length === 0) return 'A note is required';
  if (note.length > NOTE_MAX_LENGTH) {
    return `Note is ${note.length - NOTE_MAX_LENGTH} characters too long`;
  }
  return null;
}

export const DELETION_REASONS: readonly { value: UserDeletionReason; label: string }[] = [
  { value: 'USER_REQUEST_EMAIL', label: 'User asked by email' },
  { value: 'TERMS_VIOLATION', label: 'Terms violation' },
  { value: 'OTHER', label: 'Other (explain in the note)' },
];

export function isDeletionReason(value: string): value is UserDeletionReason {
  return DELETION_REASONS.some(option => option.value === value);
}

const REASON_LABELS: Record<string, string> = Object.fromEntries(
  DELETION_REASONS.map(option => [option.value, option.label])
);

/** An unknown reason is shown verbatim rather than hidden. */
export function deletionReasonLabel(reason: string | null): string {
  if (reason === null || reason === '') return '—';
  return REASON_LABELS[reason] ?? reason;
}

export function deletionRequestBlocker(draft: { reason: string; note: string }): string | null {
  if (!isDeletionReason(draft.reason)) return 'Select a reason';
  return noteBlocker(draft.note);
}

export interface ReasonConsequence {
  /** `crit` renders as a warning block, `info` as ordinary helper text. */
  readonly tone: 'crit' | 'info';
  readonly text: string;
}

/**
 * What choosing a reason will actually do, said before the operator submits.
 * TERMS_VIOLATION differs in kind, not degree: it locks the donor out and
 * takes away their own way to undo it, so it is never presented as just
 * another option in the list.
 */
export function reasonConsequence(reason: string): ReasonConsequence | null {
  if (!isDeletionReason(reason)) return null;
  if (reason === 'TERMS_VIOLATION') {
    return {
      tone: 'crit',
      text: 'The account is suspended as soon as a second operator approves this. The donor cannot cancel the deletion by signing in. The waiting period still applies — the account is not deleted any sooner.',
    };
  }
  return {
    tone: 'info',
    text: 'Once a second operator approves, the deletion is scheduled with the normal waiting period. The donor can still cancel it by signing in before the waiting period ends.',
  };
}

// ---------------------------------------------------------------------------
// Current deletion state
// ---------------------------------------------------------------------------

/**
 * How much the console knows about an account's deletion state. `error` is
 * never folded into "nothing scheduled": a failed read rendered as an absent
 * deletion tells the operator an account is safe when nobody checked.
 */
export type DeletionLookupState = 'idle' | 'loading' | 'error' | 'ready';

export function deletionLookupState(query: {
  readonly isLoading: boolean;
  readonly isError: boolean;
  readonly hasData: boolean;
}): DeletionLookupState {
  if (query.isLoading) return 'loading';
  // Checked before the data: a failed REFETCH keeps the last good data, and a
  // stale "active" is exactly what must not drive a cancel button.
  if (query.isError) return 'error';
  return query.hasData ? 'ready' : 'idle';
}

const SOURCE_LABELS: Record<string, string> = {
  app: 'the mobile app',
  web_form: 'the website form',
  admin_console: 'the admin console',
};

const ACTOR_LABELS: Record<string, string> = {
  user: 'The donor',
  admin: 'An operator',
  scheduled_worker: 'A scheduled job',
  system: 'The system',
};

/** "Who started it": actor_kind says who acted, initiation_source says where. */
export function initiatedByLabel(actorKind: string, source: string | null): string {
  const who = ACTOR_LABELS[actorKind] ?? actorKind;
  if (source === null || source === '') return who;
  return `${who}, from ${SOURCE_LABELS[source] ?? source}`;
}

/** A timestamp for display, or the fallback when absent or unparseable. */
export function formatWhen(iso: string | null | undefined, fallback = '—'): string {
  if (iso === null || iso === undefined || iso === '') return fallback;
  const parsed = new Date(iso);
  return Number.isNaN(parsed.getTime()) ? fallback : parsed.toLocaleString();
}

const DAY_MS = 86_400_000;

/**
 * The waiting period in words. Past its end the deletion has not necessarily
 * run -- the worker picks it up on its next pass -- so this says "ended", not
 * "deleted".
 */
export function waitingPeriodNote(coolingOffUntil: string | null, now: Date): string {
  if (coolingOffUntil === null) return 'No waiting period has started.';
  const end = new Date(coolingOffUntil).getTime();
  if (Number.isNaN(end)) return 'The waiting period end date could not be read.';
  const msLeft = end - now.getTime();
  if (msLeft <= 0) {
    return 'The waiting period has ended. The deletion runs on the worker’s next pass.';
  }
  if (msLeft < DAY_MS) return 'The waiting period ends in less than a day.';
  const days = Math.floor(msLeft / DAY_MS);
  return `The waiting period ends in ${days} day${days === 1 ? '' : 's'}.`;
}

export interface DeletionStateRow {
  readonly label: string;
  readonly value: string;
}

/** The active request as label/value rows; empty when nothing is scheduled. */
export function deletionStateRows(state: UserDeletionState): DeletionStateRow[] {
  const request = state.request;
  if (!state.active || request === null) return [];
  return [
    { label: 'Scheduled for', value: formatWhen(request.cooling_off_until) },
    { label: 'Requested', value: formatWhen(request.requested_at) },
    { label: 'Started by', value: initiatedByLabel(request.actor_kind, request.initiation_source) },
    { label: 'Operator reason', value: deletionReasonLabel(request.admin_reason) },
    {
      label: 'Donor can cancel',
      value: request.user_cancellable
        ? 'Yes — by signing in before the waiting period ends'
        : 'No — only an operator can cancel this deletion',
    },
  ];
}

export interface DeletionActionGate {
  readonly canRequest: boolean;
  readonly canCancel: boolean;
  /** What the viewer is told, whether or not they can act. */
  readonly note: string;
}

/**
 * Which deletion actions this viewer may take on one account right now.
 *
 * Both actions stay hidden until the state is actually known: offering
 * "Delete account" over an unread state could park a second deletion for an
 * account that already has one, and offering "Cancel" could promise something
 * Phase 4 has already made impossible.
 */
export function deletionActionGate(
  lookup: DeletionLookupState,
  state: UserDeletionState | null,
  viewerCanWrite: boolean,
  pending: PendingDeletionLookup = NO_PENDING_LOOKUP
): DeletionActionGate {
  const none = { canRequest: false, canCancel: false };
  if (lookup === 'idle' || lookup === 'loading') {
    return { ...none, note: 'Checking whether a deletion is scheduled…' };
  }
  if (lookup === 'error' || state === null) {
    return {
      ...none,
      note: 'The deletion state could not be loaded, so it is not safe to say whether one is scheduled. Retry before acting.',
    };
  }
  if (state.phase_4_started) {
    return {
      ...none,
      note: 'Deletion is already being carried out. It can no longer be cancelled by anyone.',
    };
  }
  if (!viewerCanWrite) {
    return {
      ...none,
      note: 'Your role has read-only access — starting or cancelling a deletion requires the admin or admin-operator role.',
    };
  }
  if (state.active) {
    return {
      canRequest: false,
      canCancel: true,
      note: 'A deletion is scheduled. It can be cancelled until the waiting period ends and the deletion starts.',
    };
  }
  return requestGate(pending);
}

/**
 * What the console knows about a `user.delete` request already waiting for a
 * second operator. `status` is the approvals-queue read, not the deletion read.
 */
export interface PendingDeletionLookup {
  readonly status: DeletionLookupState;
  readonly action: PendingActionItem | null;
}

/** For callers (and tests) that have no approvals-queue read to offer. */
export const NO_PENDING_LOOKUP: PendingDeletionLookup = { status: 'ready', action: null };

/**
 * Whether "Delete account" may be offered for an account with nothing
 * scheduled.
 *
 * Submitting only parks a request for a second operator, so the account's
 * deletion state still reads "nothing scheduled" afterwards -- and the backend
 * does not deduplicate pending requests. The approvals queue is the only
 * record that one is already open, so the button waits for that read, and an
 * unread queue is not read as an empty one.
 */
function requestGate(pending: PendingDeletionLookup): DeletionActionGate {
  const none = { canRequest: false, canCancel: false };
  if (pending.status === 'idle' || pending.status === 'loading') {
    return {
      ...none,
      note: 'Checking whether a deletion request is already waiting for approval…',
    };
  }
  if (pending.status === 'error') {
    return {
      ...none,
      note: 'The approvals queue could not be read, so it is not known whether a deletion request is already waiting for this account. Retry, or check the Approvals page before submitting another.',
    };
  }
  if (pending.action !== null) {
    return {
      ...none,
      note: `A deletion request for this account is already waiting for approval (submitted ${formatWhen(pending.action.requested_at)}). A different operator decides it on the Approvals page; nothing is scheduled until then.`,
    };
  }
  return {
    canRequest: true,
    canCancel: false,
    note: 'No deletion is scheduled for this account.',
  };
}

/** The open `user.delete` request aimed at this account, if this page holds one. */
export function pendingUserDeletionFor(
  actions: readonly PendingActionItem[],
  userId: string
): PendingActionItem | null {
  const match = actions.find(action => {
    if (action.operation !== USER_DELETE_OPERATION || action.status !== 'pending') return false;
    const payload = action.payload;
    if (typeof payload !== 'object' || payload === null) return false;
    return (payload as { user_id?: unknown }).user_id === userId;
  });
  return match ?? null;
}

export const PENDING_DELETION_LOOKUP_EXHAUSTED =
  'The approvals queue is too long to search. Check the Approvals page for a pending deletion of this account before submitting another.';

/** Said beside every cancel button: the two states are easy to conflate. */
export const CANCEL_DOES_NOT_REINSTATE_TEXT =
  'Cancelling stops the deletion and restores the status the account held before it. It does NOT reinstate a suspended account — an account suspended for a terms violation stays suspended until it is reinstated separately.';

// ---------------------------------------------------------------------------
// Approvals: the user.delete pending action
// ---------------------------------------------------------------------------

/** The pending-action operation an account deletion is gated behind. */
export const USER_DELETE_OPERATION = 'user.delete';

export interface UserDeletePayloadSummary {
  readonly rows: readonly DeletionStateRow[];
  /** What approving will do, in the same words the requester was shown. */
  readonly consequence: ReasonConsequence | null;
}

/**
 * The user.delete payload ({ user_id, reason, note }) as labelled rows, or
 * null when it is not shaped that way -- the caller then falls back to the
 * raw key/value dump, because a second operator must never be shown LESS than
 * what will execute just because this summary did not recognise it.
 *
 * The note is on the pending-action row and nowhere else (it never reaches
 * the governance ledger), so this is the one place an approver can read why.
 */
export function userDeletePayloadSummary(payload: unknown): UserDeletePayloadSummary | null {
  if (typeof payload !== 'object' || payload === null || Array.isArray(payload)) return null;
  const { user_id: userId, reason, note } = payload as Record<string, unknown>;
  if (typeof userId !== 'string' || typeof reason !== 'string') return null;

  const label = deletionReasonLabel(reason);
  return {
    rows: [
      { label: 'Account to delete', value: userId },
      { label: 'Reason', value: label === reason ? reason : `${label} (${reason})` },
      {
        label: 'Operator note',
        value: typeof note === 'string' && note.trim() !== '' ? note : 'No note was recorded.',
      },
    ],
    consequence: reasonConsequence(reason),
  };
}
